import React, { useState } from "react";
import { Box, Button, Theme, useMediaQuery, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  Banner,
  LoadingButton,
  Modal,
  RHFSelect,
  RHFTextField,
} from "@rentablo/components";
import {
  CACHE_KEYS,
  CreateApiTokenParamsPermissionsEnum,
  useCreateApiToken,
} from "@rentablo/api/apiv1";
import WarningIcon from "@icons/myicons/warning.svg?react";
import { PermissionsField } from "./PermissionsField";
import { TokenCreatedView } from "./TokenCreatedView";
import {
  CreateTokenFormValues,
  TokenValidity,
  VALIDITY_TRANSLATION_KEYS,
} from "./constants";
import { validityToExpiresAt } from "./helpers";

type Props = {
  isOpen: boolean;
  userId: number;
  onClose: () => void;
};

export const CreateApiTokenModal = ({ isOpen, userId, onClose }: Props) => {
  const { t } = useTranslation();
  const isSM = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<"form" | "success">("form");
  const [created, setCreated] = useState<{ name: string; token: string }>();
  const [errorMessage, setErrorMessage] = useState<string>();

  const { mutate: createToken, isPending, reset } = useCreateApiToken();

  const schema = Yup.object().shape({
    name: Yup.string(),
    permissions: Yup.array()
      .of(Yup.mixed<CreateApiTokenParamsPermissionsEnum>().defined())
      .min(1, t("settings.apiAccess.createModal.permissionsError"))
      .defined(),
    validity: Yup.mixed<TokenValidity>().defined(),
  });

  const { control, handleSubmit } = useForm<CreateTokenFormValues>({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      permissions: [CreateApiTokenParamsPermissionsEnum.ACCOUNTS_READ],
      validity: "90",
    },
  });

  const validityOptions = Object.entries(VALIDITY_TRANSLATION_KEYS).reduce<
    Record<string, string>
  >((options, [value, key]) => {
    options[value] = t(`settings.apiAccess.createModal.validity.${key}`);
    return options;
  }, {});

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: CreateTokenFormValues) => {
    setErrorMessage(undefined);
    createToken(
      {
        userId,
        createApiTokenParams: {
          name:
            data.name?.trim() ||
            t("settings.apiAccess.createModal.unnamedToken"),
          permissions: data.permissions,
          expiresAt: validityToExpiresAt(data.validity),
        },
      },
      {
        onSuccess: (response) => {
          setCreated({
            name: response.name ?? "",
            token: response.token ?? "",
          });
          setStep("success");
          queryClient.invalidateQueries({
            queryKey: [CACHE_KEYS.LIST_API_TOKENS],
          });
        },
        onError: (err) => {
          setErrorMessage(
            err.response?.data?.message ||
              t("settings.apiAccess.createModal.error")
          );
        },
      }
    );
  };

  const isSuccess = step === "success";

  return (
    <Modal
      isOpen={isOpen}
      title={
        isSuccess
          ? t("settings.apiAccess.createdModal.title")
          : t("settings.apiAccess.createModal.title")
      }
      subtitle={
        isSuccess
          ? t("settings.apiAccess.createdModal.subtitle", {
              name: created?.name,
            })
          : t("settings.apiAccess.createModal.subtitle")
      }
      width={620}
      height="auto"
      maxHeight={isSM ? "unset" : "calc(100% - 30px)"}
      onClose={isSuccess ? undefined : handleClose}
    >
      {isSuccess ? (
        <TokenCreatedView token={created?.token ?? ""} onDone={handleClose} />
      ) : (
        <>
          <Box display="flex" flexDirection="column" gap={6}>
            {errorMessage && (
              <Banner
                type="error"
                icon={<WarningIcon color={theme.palette.error.dark} />}
                text={errorMessage}
              />
            )}
            <RHFTextField
              control={control}
              name="name"
              label={t("settings.apiAccess.createModal.nameLabel")}
              placeholder={t("settings.apiAccess.createModal.namePlaceholder")}
              helperText={t("settings.apiAccess.createModal.nameHint")}
              fullWidth
              autoFocus
            />
            <PermissionsField control={control} />
            <Box maxWidth={260}>
              <RHFSelect
                control={control}
                name="validity"
                label={t("settings.apiAccess.createModal.validityLabel")}
                options={validityOptions}
              />
            </Box>
          </Box>
          <Box display="flex" justifyContent="flex-end" gap={4} mt={11}>
            <Button variant="outlined" onClick={handleClose}>
              {t("settings.apiAccess.createModal.cancel")}
            </Button>
            <LoadingButton
              isFetching={isPending}
              variant="primary"
              onClick={handleSubmit(onSubmit)}
            >
              {t("settings.apiAccess.createModal.submit")}
            </LoadingButton>
          </Box>
        </>
      )}
    </Modal>
  );
};
